import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import StorageIcon from '@mui/icons-material/Storage';
import StoreIcon from '@mui/icons-material/Store';
import HandshakeIcon from '@mui/icons-material/Handshake';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TouchAppIcon from '@mui/icons-material/TouchApp';

interface TutorialSubStep {
  id: string;
  instruction: string;
  targetSelector: string;
  highlightText?: string;
  waitForAction?: boolean;
  hoverAdvance?: boolean;
}

interface TutorialStep {
  id: string;
  title: string;
  description: string;
  targetSelector: string;
  targetRoute: string;
  icon: React.ReactNode;
  actionLabel: string;
  completedLabel: string;
  checkCompletion: () => boolean;
  subSteps?: TutorialSubStep[];
  infoMessage?: string;
}

interface DePINHubTutorialProps {
  isActive: boolean;
  onClose: () => void;
  onComplete: () => void;
  currentStep: number;
  onStepChange: (step: number) => void;
  isWalletConnected: boolean;
  hasBuckets: boolean;
  hasMarketplaceVisited: boolean;
  hasDeals: boolean;
}

const DePINHubTutorial: React.FC<DePINHubTutorialProps> = ({
  isActive,
  onClose,
  onComplete,
  currentStep,
  onStepChange,
  isWalletConnected,
  hasBuckets,
  hasMarketplaceVisited,
  hasDeals,
}) => {
  const [highlightRect, setHighlightRect] = useState<DOMRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [subStepIndex, setSubStepIndex] = useState(0);
  const [showNavigationPrompt, setShowNavigationPrompt] = useState(false);
  const [elementNotFound, setElementNotFound] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const lastAdvanceTimeRef = useRef<number>(0);

  const navigate = useNavigate();
  const location = useLocation();

  // Tutorial steps for DePIN Hub
  const steps: TutorialStep[] = [
    {
      id: 'connect-wallet',
      title: 'Connect Your Wallet',
      description: 'Connect your Stellar wallet to start using ISP Market.',
      targetSelector: '[data-tutorial="connect-wallet"]',
      targetRoute: '/depin-hub/storage-units',
      icon: <AccountBalanceWalletIcon sx={{ fontSize: 24, color: 'white' }} />,
      actionLabel: 'Click "Connect Wallet" in the header',
      completedLabel: 'Wallet connected!',
      checkCompletion: () => isWalletConnected,
      subSteps: [
        { id: 'click-connect', instruction: 'Click "Connect Wallet" button in the header', targetSelector: '[data-tutorial="connect-wallet"]' },
      ],
    },
    {
      id: 'register-bucket',
      title: 'Register a Bucket',
      description: 'Add your first storage bucket to offer storage capacity on the network.',
      targetSelector: '[data-tutorial="add-bucket"]',
      targetRoute: '/depin-hub/storage-units',
      icon: <StorageIcon sx={{ fontSize: 24, color: 'white' }} />,
      actionLabel: 'Click the + button to add a bucket',
      completedLabel: 'Bucket registered!',
      checkCompletion: () => hasBuckets,
      subSteps: [
        { id: 'click-add-bucket', instruction: 'Click the + button to add a storage unit', targetSelector: '[data-tutorial="add-bucket"]' },
        { id: 'select-provider', instruction: 'Select a storage provider', targetSelector: '[data-tutorial="provider-card"]' },
        { id: 'fill-details', instruction: 'Fill in the bucket details', targetSelector: '[data-tutorial="bucket-name"]' },
        { id: 'submit-bucket', instruction: 'Click "Create Bucket" to register', targetSelector: '[data-tutorial="submit-bucket"]', waitForAction: true },
      ],
    },
    {
      id: 'browse-marketplace',
      title: 'Browse the Marketplace',
      description: 'Explore available storage offers from other providers.',
      targetSelector: '[data-tutorial="menu-marketplace"]',
      targetRoute: '/depin-hub/storage-units',
      icon: <StoreIcon sx={{ fontSize: 24, color: 'white' }} />,
      actionLabel: 'Navigate to Marketplace',
      completedLabel: 'Marketplace visited!',
      checkCompletion: () => hasMarketplaceVisited,
      subSteps: [
        { id: 'click-marketplace', instruction: 'Click on "Marketplace" in the sidebar', targetSelector: '[data-tutorial="menu-marketplace"]' },
        { id: 'browse-offers', instruction: 'Browse available storage offers', targetSelector: '[data-tutorial="marketplace-list"]' },
      ],
    },
    {
      id: 'create-deal',
      title: 'Create Your First Deal',
      description: 'Purchase storage capacity and create your first deal.',
      targetSelector: '[data-tutorial="create-deal"]',
      targetRoute: '/depin-hub/marketplace',
      icon: <HandshakeIcon sx={{ fontSize: 24, color: 'white' }} />,
      actionLabel: 'Create a storage deal',
      completedLabel: 'Congratulations! Tutorial completed!',
      checkCompletion: () => hasDeals,
      subSteps: [
        { id: 'select-offer', instruction: 'Select a storage offer', targetSelector: '[data-tutorial="offer-card"]' },
        { id: 'create-deal', instruction: 'Click "Create Deal" to purchase', targetSelector: '[data-tutorial="create-deal-btn"]', waitForAction: true },
      ],
    },
  ];

  const currentStepData = steps[currentStep];
  const isStepCompleted = currentStepData?.checkCompletion() || false;
  const isLastStep = currentStep === steps.length - 1;
  
  const currentSubSteps = currentStepData?.subSteps || [];
  const currentSubStep = currentSubSteps[subStepIndex];
  const activeTargetSelector = currentSubStep?.targetSelector || currentStepData?.targetSelector;
  
  const navigatedStepRef = useRef<number>(-1);

  // Navigation prompt logic
  useEffect(() => {
    if (isActive && currentStepData && navigatedStepRef.current !== currentStep) {
      navigatedStepRef.current = currentStep;
      if (location.pathname !== currentStepData.targetRoute) {
        setShowNavigationPrompt(true);
      } else {
        setShowNavigationPrompt(false);
      }
    }
  }, [isActive, currentStep, currentStepData, location.pathname]);
  
  useEffect(() => {
    if (isActive && currentStepData) {
      setShowNavigationPrompt(location.pathname !== currentStepData.targetRoute);
    }
  }, [location.pathname, isActive, currentStepData]);
  
  useEffect(() => {
    if (!isActive) {
      navigatedStepRef.current = -1;
      setSubStepIndex(0);
      setShowNavigationPrompt(false);
    }
  }, [isActive]);
  
  useEffect(() => {
    setSubStepIndex(0);
  }, [currentStep]);
  
  const handleNavigateToStep = () => {
    if (currentStepData) {
      navigate(currentStepData.targetRoute);
      setShowNavigationPrompt(false);
    }
  };

  // Find and highlight target element
  const updateHighlight = useCallback(() => {
    if (!isActive || !activeTargetSelector) return;

    const target = document.querySelector(activeTargetSelector);
    if (target) {
      const rect = target.getBoundingClientRect();
      setHighlightRect(rect);

      const tooltipHeight = tooltipRef.current?.offsetHeight || 200;
      const tooltipWidth = 300;
      
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const margin = 16;
      const gap = 12;

      const hasOpenModal = document.querySelector('[role="dialog"], [role="presentation"], .MuiDialog-root, .MuiModal-root');
      
      let top: number;
      let left: number;
      
      const isSidebar = rect.left < 300;
      const isInModal = hasOpenModal && hasOpenModal.contains(target);

      if (isSidebar && !isInModal) {
        top = rect.top;
        left = rect.right + gap;
        
        if (left + tooltipWidth > viewportWidth - margin) {
          left = viewportWidth - tooltipWidth - margin;
        }
        if (top + tooltipHeight > viewportHeight - margin) {
          top = viewportHeight - tooltipHeight - margin;
        }
      } else if (isInModal) {
        const modalRect = hasOpenModal.getBoundingClientRect();
        
        if (modalRect.right + gap + tooltipWidth < viewportWidth - margin) {
          left = modalRect.right + gap;
          top = modalRect.top + 50;
        } 
        else if (modalRect.left - gap - tooltipWidth > margin) {
          left = modalRect.left - tooltipWidth - gap;
          top = modalRect.top + 50;
        }
        else {
          top = viewportHeight - tooltipHeight - margin - 60;
          left = viewportWidth - tooltipWidth - margin;
        }
      } else {
        if (rect.right + gap + tooltipWidth < viewportWidth - margin) {
          left = rect.right + gap;
          top = rect.top;
        }
        else if (rect.left - gap - tooltipWidth > margin + 300) {
          left = rect.left - tooltipWidth - gap;
          top = rect.top;
        }
        else if (rect.bottom + gap + tooltipHeight < viewportHeight - margin) {
          left = Math.max(margin + 300, Math.min(rect.left, viewportWidth - tooltipWidth - margin));
          top = rect.bottom + gap;
        }
        else if (rect.top - gap - tooltipHeight > margin) {
          left = Math.max(margin + 300, Math.min(rect.left, viewportWidth - tooltipWidth - margin));
          top = rect.top - tooltipHeight - gap;
        }
        else {
          top = viewportHeight - tooltipHeight - margin - 60;
          left = viewportWidth - tooltipWidth - margin;
        }
      }

      if (top < margin) top = margin;
      if (top + tooltipHeight > viewportHeight - margin) {
        top = viewportHeight - tooltipHeight - margin;
      }
      if (left < margin) left = margin;
      if (left + tooltipWidth > viewportWidth - margin) {
        left = viewportWidth - tooltipWidth - margin;
      }

      setTooltipPosition({ top, left });
      setElementNotFound(false);
    } else {
      setHighlightRect(null);
      setElementNotFound(true);
      
      const tooltipHeight = tooltipRef.current?.offsetHeight || 200;
      const tooltipWidth = 300;
      
      const top = window.innerHeight - tooltipHeight - 12 - 80;
      const left = window.innerWidth - tooltipWidth - 12;
      
      setTooltipPosition({ 
        top: Math.max(12, top), 
        left: Math.max(12, left) 
      });
    }
  }, [isActive, activeTargetSelector, isStepCompleted, showNavigationPrompt, subStepIndex]);
  
  // Listen for clicks to advance sub-steps
  useEffect(() => {
    if (!isActive || !activeTargetSelector) return;
    
    const canAdvance = () => {
      const now = Date.now();
      if (now - lastAdvanceTimeRef.current < 800) {
        return false;
      }
      lastAdvanceTimeRef.current = now;
      return true;
    };
    
    const handleClick = (e: MouseEvent) => {
      const target = document.querySelector(activeTargetSelector);
      if (target && (target === e.target || target.contains(e.target as Node))) {
        if (currentSubSteps.length > 0 && subStepIndex < currentSubSteps.length - 1) {
          if (canAdvance()) {
            setSubStepIndex(prev => prev + 1);
          }
        }
      }
    };
    
    const handleHover = (e: MouseEvent) => {
      if (!currentSubStep?.hoverAdvance) return;
      
      const target = document.querySelector(activeTargetSelector);
      if (target && (target === e.target || target.contains(e.target as Node))) {
        if (currentSubSteps.length > 0 && subStepIndex < currentSubSteps.length - 1) {
          if (canAdvance()) {
            setSubStepIndex(prev => prev + 1);
          }
        }
      }
    };
    
    document.addEventListener('click', handleClick, true);
    document.addEventListener('mouseenter', handleHover, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('mouseenter', handleHover, true);
    };
  }, [isActive, activeTargetSelector, currentSubSteps.length, subStepIndex, currentSubStep]);

  useEffect(() => {
    updateHighlight();
    window.addEventListener('resize', updateHighlight);
    window.addEventListener('scroll', updateHighlight);

    const interval = setInterval(updateHighlight, 500);

    return () => {
      window.removeEventListener('resize', updateHighlight);
      window.removeEventListener('scroll', updateHighlight);
      clearInterval(interval);
    };
  }, [updateHighlight]);

  // Auto-advance when step completed
  useEffect(() => {
    if (isActive && isStepCompleted && !isLastStep) {
      const timer = setTimeout(() => {
        const nextStep = steps[currentStep + 1];
        if (nextStep?.targetRoute && location.pathname !== nextStep.targetRoute) {
          navigate(nextStep.targetRoute);
        }
        onStepChange(currentStep + 1);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isActive, isStepCompleted, isLastStep, currentStep, onStepChange, navigate, location.pathname, steps]);

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
    } else if (isStepCompleted) {
      onStepChange(currentStep + 1);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  if (!isActive) return null;

  const padding = 8;
  const holeTop = highlightRect ? highlightRect.top - padding : 0;
  const holeLeft = highlightRect ? highlightRect.left - padding : 0;
  const holeWidth = highlightRect ? highlightRect.width + padding * 2 : 0;
  const holeHeight = highlightRect ? highlightRect.height + padding * 2 : 0;

  return (
    <>
      {/* Highlight ring */}
      {highlightRect && (
        <Box
          sx={{
            position: 'fixed',
            top: holeTop,
            left: holeLeft,
            width: holeWidth,
            height: holeHeight,
            borderRadius: '12px',
            boxShadow: '0 0 0 3px rgba(97, 123, 255, 0.5), 0 0 20px rgba(97, 123, 255, 0.3)',
            animation: 'guided-pulse-light 2s ease-in-out infinite',
            '@keyframes guided-pulse-light': {
              '0%': { boxShadow: '0 0 0 3px rgba(97, 123, 255, 0.5), 0 0 20px rgba(97, 123, 255, 0.3)' },
              '50%': { boxShadow: '0 0 0 6px rgba(97, 123, 255, 0.3), 0 0 30px rgba(97, 123, 255, 0.4)' },
              '100%': { boxShadow: '0 0 0 3px rgba(97, 123, 255, 0.5), 0 0 20px rgba(97, 123, 255, 0.3)' },
            },
            pointerEvents: 'none',
            zIndex: 9998,
          }}
        />
      )}

      {/* Tooltip card */}
      <Box
        ref={tooltipRef}
        sx={{
          position: 'fixed',
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          width: 300,
          bgcolor: 'white',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
          zIndex: 9999,
          overflow: 'hidden',
          transition: 'top 0.3s cubic-bezier(0.4, 0, 0.2, 1), left 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease',
          opacity: tooltipPosition.top === 0 ? 0 : 1,
          border: '1px solid #E5E7EB',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #E5E7EB',
            bgcolor: '#F9FAFB',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '6px',
                bgcolor: '#617BFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}
            >
              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: 'white' }}>
                {currentStep + 1}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#101828' }}>
              {currentStepData?.title} {currentSubSteps.length > 1 && `(${subStepIndex + 1}/${currentSubSteps.length})`}
            </Typography>
          </Box>
          <IconButton 
            size="small" 
            onClick={handleSkip} 
            sx={{ 
              p: 0.5,
              color: '#9CA3AF', 
              '&:hover': { color: '#667085', bgcolor: '#F3F4F6' } 
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              borderRadius: '8px',
              bgcolor: isStepCompleted ? '#F0FDF4' : elementNotFound ? '#FEF3C7' : '#F5F7FF',
              border: '1px solid',
              borderColor: isStepCompleted ? '#BBF7D0' : elementNotFound ? '#FCD34D' : '#E0E7FF',
            }}
          >
            {isStepCompleted ? (
              <CheckCircleIcon sx={{ fontSize: 18, color: '#10B981' }} />
            ) : elementNotFound ? (
              <InfoOutlinedIcon sx={{ fontSize: 18, color: '#F59E0B' }} />
            ) : (
              <TouchAppIcon sx={{ fontSize: 18, color: '#617BFF' }} />
            )}
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 500,
                color: isStepCompleted ? '#15803D' : elementNotFound ? '#92400E' : '#374151',
                flex: 1,
              }}
            >
              {isStepCompleted 
                ? currentStepData?.completedLabel 
                : elementNotFound
                  ? 'Element not visible. Navigate to the correct page.'
                  : showNavigationPrompt
                    ? `Navigate to ${currentStepData?.title}`
                    : currentSubStep?.instruction || currentStepData?.actionLabel
              }
            </Typography>
          </Box>
          
          {showNavigationPrompt && !isStepCompleted && (
            <Box
              onClick={handleNavigateToStep}
              sx={{
                mt: 1.5,
                width: '100%',
                textAlign: 'center',
                py: 1,
                bgcolor: '#617BFF',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                color: 'white',
                cursor: 'pointer',
                '&:hover': { bgcolor: '#5068e0' },
              }}
            >
              Take me there
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: '1px solid #E5E7EB',
          }}
        >
          <Typography
            onClick={handleSkip}
            sx={{
              fontSize: '12px',
              color: '#9CA3AF',
              cursor: 'pointer',
              '&:hover': { color: '#667085' },
            }}
          >
            Skip
          </Typography>

          {/* Progress dots */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {steps.map((_, idx) => (
              <Box
                key={idx}
                sx={{
                  width: idx === currentStep ? 16 : 6,
                  height: 6,
                  borderRadius: 3,
                  bgcolor: idx <= currentStep ? '#617BFF' : '#E5E7EB',
                  transition: 'all 0.2s ease',
                }}
              />
            ))}
          </Box>

          {isStepCompleted ? (
            <Box
              onClick={handleNext}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: '#617BFF',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': { color: '#4A67E3' },
              }}
            >
              {isLastStep ? 'Done' : 'Next'}
              {!isLastStep && <ArrowForwardIcon sx={{ fontSize: 14 }} />}
            </Box>
          ) : (
            <Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>
              {currentStep + 1}/{steps.length}
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
};

export default DePINHubTutorial;
